/**
 * Wire format, version 1.
 *
 * **Frozen.** Once a version ships it is never edited — a new version and a migration is
 * the only way forward. Teachers' files have to keep opening, and the cheapest way to
 * guarantee that is to make the old shape unchangeable rather than carefully compatible.
 *
 * ## Shape decisions
 *
 * - **Points are `[x, y]`, not `{x, y}`.** A construction is mostly coordinates, and the
 *   array form is roughly half the bytes. Autosave writes to a storage quota measured in
 *   megabytes, so this is not a micro-optimisation.
 * - **Styles live in a palette.** Entities reference one by index. A diagram of five
 *   thousand entities typically uses under a dozen distinct styles; inlining them would
 *   make style data dominate the file.
 * - **Defaults are omitted.** `visible`, `locked`, `isConstruction` and `zIndex` are
 *   absent when they hold their default value, which is the overwhelming majority.
 * - **Only the document is stored.** No evaluated coordinates, no bounding boxes, no
 *   measurement results — all of that is recomputed on load. A file therefore describes
 *   a construction rather than a snapshot of its appearance, which is what lets a newer
 *   evaluator correct an older file rather than inherit its mistakes.
 */

export const FORMAT_TAG = 'edusketch';
export const SCHEMA_VERSION_1 = 1;

/** `[x, y]`. */
export type WireVec2 = readonly [number, number];

export interface WireStyle {
  readonly stroke: string;
  readonly strokeWidth: number;
  readonly lineStyle: string;
  readonly fill: string;
  readonly fillStyle: string;
  readonly opacity: number;
  readonly pointStyle: string;
  readonly pointSize: number;
}

export interface WireEntity {
  readonly id: string;
  /** The construction, with `Vec2` fields in wire form. */
  readonly source: Readonly<Record<string, unknown>>;
  /** Index into the document's style palette. */
  readonly style: number;
  /** Omitted when 0. */
  readonly z?: number;
  /** Omitted when true. */
  readonly visible?: boolean;
  /** Omitted when false. */
  readonly locked?: boolean;
  /** Omitted when false. */
  readonly construction?: boolean;
  readonly label?: string;
}

export interface WireConstraint {
  readonly id: string;
  readonly kind: string;
  readonly [field: string]: unknown;
}

export interface WireDocumentV1 {
  readonly format: typeof FORMAT_TAG;
  readonly schemaVersion: number;
  readonly title: string;
  readonly metadata: Readonly<Record<string, string>>;
  readonly nextId: number;
  readonly styles: readonly WireStyle[];
  readonly entities: readonly WireEntity[];
  readonly constraints: readonly WireConstraint[];
}

/** The version this build writes. */
export const CURRENT_SCHEMA_VERSION = SCHEMA_VERSION_1;
